import React, { useRef, useEffect, useState } from 'react';
import { ImageIcon, Loader2, AlertCircle } from 'lucide-react';

export default function ModelPreview({ 
  src, 
  className = "w-full h-full", 
  category = "tops", 
  posterUrl = null, 
  isModalOpen = true,
  onLoad = () => {},
  onError = () => {}
}) {
  const canvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showThumbnail, setShowThumbnail] = useState(false);

  // Check if we have a valid 3D model URL
  const hasValidModel = src && (src.includes('.glb') || src.includes('.gltf'));

  useEffect(() => {
    if (!hasValidModel) {
      setError('No valid 3D model URL provided');
      setIsLoading(false);
      onError('No valid 3D model URL provided');
      return;
    }

    setIsLoading(true);
    setError(null);

    // For now, we'll show a placeholder since we need to set up Three.js properly
    // In a full implementation, you would load the GLB/GLTF file here
    setTimeout(() => {
      setIsLoading(false);
      onLoad();
    }, 1000);

  }, [src, hasValidModel, onLoad, onError]);

  // If we're showing thumbnail or no valid model, show the poster image
  if (showThumbnail || !hasValidModel) {
    return (
      <div className={`${className} relative bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden`}>
        {posterUrl ? (
          <img
            src={posterUrl}
            alt="3D Model Preview"
            className="w-full h-full object-cover"
            onError={() => setError('Failed to load preview image')}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center text-gray-400">
              <ImageIcon size={32} className="mx-auto mb-2 text-gray-500" />
              <div className="text-gray-300 text-sm">No Preview Available</div>
              <div className="text-gray-500 text-xs mt-1">Upload a 3D model to see preview</div>
            </div>
          </div>
        )}
        
        {/* Toggle Button */}
        {hasValidModel && (
          <button
            onClick={() => setShowThumbnail(!showThumbnail)}
            className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-all duration-200 backdrop-blur-sm"
            title={showThumbnail ? "Show 3D Model" : "Show Thumbnail"}
          >
            {showThumbnail ? (
              <div className="w-4 h-4 flex items-center justify-center">
                <div className="w-3 h-3 bg-white rounded-sm"></div>
              </div>
            ) : (
              <ImageIcon size={16} />
            )}
          </button>
        )}
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={`${className} bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center`}>
        <div className="text-center text-gray-400">
          <Loader2 size={32} className="mx-auto mb-2 text-blue-400 animate-spin" />
          <div className="text-gray-300 text-sm">Loading 3D Model...</div>
          <div className="text-gray-500 text-xs mt-1">This may take a moment</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`${className} bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center`}>
        <div className="text-center text-gray-400">
          <AlertCircle size={32} className="mx-auto mb-2 text-red-400" />
          <div className="text-gray-300 text-sm">Failed to Load 3D Model</div>
          <div className="text-gray-500 text-xs mt-1">{error}</div>
          {hasValidModel && (
            <button
              onClick={() => setShowThumbnail(true)}
              className="mt-2 text-blue-400 hover:text-blue-300 text-xs underline"
            >
              Show thumbnail instead
            </button>
          )}
        </div>
      </div>
    );
  }

  // 3D Model display (placeholder for now)
  return (
    <div className={`${className} relative bg-gradient-to-br from-gray-800 to-gray-900`}>
      {/* 3D Model Container */}
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center text-gray-400">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-700 rounded-lg flex items-center justify-center">
            <div className="w-8 h-8 bg-gray-600 rounded"></div>
          </div>
          <div className="text-gray-300 text-sm">3D Model Loaded</div>
          <div className="text-gray-500 text-xs mt-1">
            {src ? `Source: ${src.split('/').pop()}` : 'No source provided'}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center">
        <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-1">
          <span className="text-white text-xs">3D Model</span>
        </div>
        
        <button
          onClick={() => setShowThumbnail(true)}
          className="bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-all duration-200 backdrop-blur-sm"
          title="Show Thumbnail"
        >
          <ImageIcon size={16} />
        </button>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setShowThumbnail(!showThumbnail)}
        className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-all duration-200 backdrop-blur-sm"
        title="Toggle View"
      >
        <ImageIcon size={16} />
      </button>
    </div>
  );
}